<template>
  <component :is="showAsCard ? RevCard : 'div'" class="w-full">
    <article
      class="relative flex flex-col"
      :class="{
        'shadow-short rounded-lg h-[480px] overflow-hidden md:h-[560px]':
          showAsCard,
      }"
    >
      <div class="relative">
        <!-- eslint-disable tailwindcss/no-custom-classname -->
        <RevIllustration
          :alt="imageAlt"
          class="w-full object-cover"
          :class="[`grade-gradient-${grade}`, { 'rounded-lg': !showAsCard }]"
          :height="352"
          :src="imageSrc"
          :width="352"
        />
        <!-- eslint-enable tailwindcss/no-custom-classname -->
        <div
          class="text-static-light-hi caption absolute bottom-24 right-32 flex flex-row space-x-6"
        >
          <span>{{ i18n(translations.sampleImage) }}</span>
          <IconCamera size="16" />
        </div>
      </div>
      <div
        class="flex flex-1 flex-col items-start justify-between py-12"
        :class="{
          'bg-float-default-low px-20 ': showAsCard,
        }"
      >
        <div class="w-full space-y-12">
          <h4 class="heading-2 line-clamp-2 flex flex-row items-center">
            {{ title }}
            <component :is="titleIcon" class="ml-4" size="24" />
          </h4>
          <ul class="flex flex-wrap">
            <li
              v-for="(pill, index) in pills"
              :key="index"
              class="mb-8 mr-8 flex"
            >
              <ProductConditionPills :icon="pill.icon" :text="pill.text" />
            </li>
          </ul>
          <p class="text-static-default-mid body-2 mt-8 text-start">
            {{ content }}
          </p>
        </div>
      </div>
    </article>
  </component>
</template>

<script setup lang="ts">
import type { Component } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { IconCamera } from '@ds/icons/IconCamera'

import translations from './ProductConditionCard.translations'
import ProductConditionPills from './components/ProductConditionPills/ProductConditionPills.vue'

withDefaults(
  defineProps<{
    title: string
    titleIcon?: Component
    pills: { icon: Component; text: string }[]
    content: string
    imageAlt?: string
    imageSrc: string
    grade: number
    showAsCard?: boolean
  }>(),
  {
    titleIcon: undefined,
    imageAlt: '',
    showAsCard: true,
  },
)

const i18n = useI18n()
</script>

<style scoped>
/* Grade gradient background */
/* PREMIUM */
:deep(.grade-gradient-9) {
  background:
    linear-gradient(236deg, rgba(0, 0, 0, 0) 11.69%, #000 91.87%), #686a6e;
}
/* EXCELLENT */
:deep(.grade-gradient-10) {
  background:
    linear-gradient(146deg, rgba(143, 162, 223, 0) 7.32%, #cce3ff 87.5%),
    linear-gradient(180deg, #8fa2de 0%, #c4b8f7 100%);
}
/* GOOD */
:deep(.grade-gradient-11) {
  background:
    linear-gradient(146deg, rgba(162, 105, 232, 0) 7.32%, #ffccfd 87.5%),
    linear-gradient(180deg, #a269e8 0%, #e2b8f7 100%);
}
/* FAIR */
:deep(.grade-gradient-12) {
  background:
    linear-gradient(
      145deg,
      rgba(208, 132, 195, 0) 0.71%,
      rgba(232, 222, 197, 0.9) 86.15%
    ),
    linear-gradient(180deg, #d084c4 0%, #e1b2de 100%);
}
</style>
